import * as THREE from 'three';

/**
 * PlayerModel - A low-poly humanoid character model 
 * with basic animation capabilities and weapon attachment points.
 */
export class PlayerModel extends THREE.Group {
    constructor(options = {}) {
        super();
        
        // Configuration options with defaults
        this.config = {
            bodyColor: options.bodyColor || 0x3366cc,
            headColor: options.headColor || 0xe0ac69,
            limbColor: options.limbColor || 0x2b5797,
            backpackColor: options.backpackColor || 0x333333, // Color for visual back indicator
            scale: options.scale || 0.7, // Reduced default scale to make model smaller
            animationSpeed: options.animationSpeed || 1.0
        };
        
        // Set render order to ensure player renders behind UI
        this.renderOrder = -100;
        
        // Animation state
        this.animationState = {
            walking: false,
            aiming: false,
            shooting: false,
            idle: true,
            time: 0
        };
        
        // Aiming and shooting animation cooldown system
        this.aimingTimeout = null;
        this.shootingTimeout = null;
        this.isCurrentlyAiming = false;
        this.isCurrentlyShooting = false;
        
        // Create body parts
        this.createBodyParts();
        
        // Set up structure
        this.assembleModel();
        
        // Apply initial scale
        this.scale.set(this.config.scale, this.config.scale, this.config.scale);

        // Add an AABB bounding box for debugging
        if (options.debug) {
            const bbox = new THREE.Box3().setFromObject(this);
            const bboxHelper = new THREE.Box3Helper(bbox, 0xff0000);
            this.add(bboxHelper);
        }
    }
    
    /**
     * Create all individual body part meshes
     */
    createBodyParts() {
        // Create materials
        this.bodyMaterial = new THREE.MeshStandardMaterial({ 
            color: this.config.bodyColor,
            roughness: 0.7,
            metalness: 0.1
        });
        
        this.headMaterial = new THREE.MeshStandardMaterial({ 
            color: this.config.headColor,
            roughness: 0.7,
            metalness: 0.1
        });
        
        this.limbMaterial = new THREE.MeshStandardMaterial({ 
            color: this.config.limbColor,
            roughness: 0.7,
            metalness: 0.1
        });
        
        this.backpackMaterial = new THREE.MeshStandardMaterial({
            color: this.config.backpackColor,
            roughness: 0.8,
            metalness: 0.1
        });
        
        // Head
        this.head = new THREE.Mesh(
            new THREE.BoxGeometry(0.6, 0.6, 0.6),
            this.headMaterial
        );
        this.head.renderOrder = -100;
        
        // Torso
        this.torso = new THREE.Mesh(
            new THREE.BoxGeometry(0.8, 1.2, 0.4),
            this.bodyMaterial
        );
        this.torso.renderOrder = -100;
        
        // Backpack
        this.backpack = new THREE.Mesh(
            new THREE.BoxGeometry(0.6, 0.8, 0.2),
            this.backpackMaterial
        );
        this.backpack.renderOrder = -100;
        
        // Arms
        this.upperRightArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.25, 0.7, 0.25),
            this.limbMaterial
        );
        this.upperRightArm.renderOrder = -100;
        
        this.lowerRightArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 0.6, 0.2),
            this.limbMaterial
        );
        this.lowerRightArm.renderOrder = -100;
        
        this.upperLeftArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.25, 0.7, 0.25),
            this.limbMaterial
        );
        this.upperLeftArm.renderOrder = -100;
        
        this.lowerLeftArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 0.6, 0.2),
            this.limbMaterial
        );
        this.lowerLeftArm.renderOrder = -100;
        
        // Legs
        this.upperRightLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.25, 0.7, 0.25),
            this.limbMaterial
        );
        this.upperRightLeg.renderOrder = -100;
        
        this.lowerRightLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 0.6, 0.2),
            this.limbMaterial
        );
        this.lowerRightLeg.renderOrder = -100;
        
        this.upperLeftLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.25, 0.7, 0.25),
            this.limbMaterial
        );
        this.upperLeftLeg.renderOrder = -100;
        
        this.lowerLeftLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 0.6, 0.2),
            this.limbMaterial
        );
        this.lowerLeftLeg.renderOrder = -100;
        
        // Create weapon attachment point (empty object)
        this.weaponAttachment = new THREE.Object3D();
    }
    
    /**
     * Assemble the model with proper hierarchy
     */
    assembleModel() {
        // Create joints (empty groups for rotation)
        this.neck = new THREE.Group();
        this.rightShoulder = new THREE.Group();
        this.leftShoulder = new THREE.Group();
        this.rightElbow = new THREE.Group();
        this.leftElbow = new THREE.Group();
        this.rightHip = new THREE.Group();
        this.leftHip = new THREE.Group();
        this.rightKnee = new THREE.Group();
        this.leftKnee = new THREE.Group();
        
        // Add parts to the model with proper hierarchy
        
        // Head and neck
        this.neck.position.set(0, 0.8, 0);
        this.neck.add(this.head);
        this.head.position.set(0, 0.35, 0);
        this.torso.add(this.neck);
        
        // Add backpack to the back of the torso
        this.backpack.position.set(0, 0, -0.3); // Position on the back
        this.torso.add(this.backpack);
        
        // Right arm (from player's perspective)
        this.rightShoulder.position.set(-0.5, 0.6, 0);
        this.rightShoulder.add(this.upperRightArm);
        this.upperRightArm.position.set(0, -0.35, 0);
        this.torso.add(this.rightShoulder);
        
        this.rightElbow.position.set(0, -0.7, 0);
        this.rightElbow.add(this.lowerRightArm);
        this.lowerRightArm.position.set(0, -0.3, 0);
        this.upperRightArm.add(this.rightElbow);
        
        // Add weapon attachment to the right hand where the pistol handle would be
        // Position it so the player grabs the pistol handle, not the middle of the gun
        this.weaponAttachment.position.set(0.1, -0.35, 0.1);
        // Rotate to get proper orientation so the handle aligns with the hand
        this.weaponAttachment.rotation.set(0, Math.PI/2, 0);
        this.lowerRightArm.add(this.weaponAttachment);
        
        // Left arm
        this.leftShoulder.position.set(0.5, 0.6, 0);
        this.leftShoulder.add(this.upperLeftArm);
        this.upperLeftArm.position.set(0, -0.35, 0);
        this.torso.add(this.leftShoulder);
        
        this.leftElbow.position.set(0, -0.7, 0);
        this.leftElbow.add(this.lowerLeftArm);
        this.lowerLeftArm.position.set(0, -0.3, 0);
        this.upperLeftArm.add(this.leftElbow);
        
        // Right leg
        this.rightHip.position.set(-0.25, -0.6, 0);
        this.rightHip.add(this.upperRightLeg);
        this.upperRightLeg.position.set(0, -0.35, 0);
        this.torso.add(this.rightHip);
        
        this.rightKnee.position.set(0, -0.7, 0);
        this.rightKnee.add(this.lowerRightLeg);
        this.lowerRightLeg.position.set(0, -0.3, 0);
        this.upperRightLeg.add(this.rightKnee);
        
        // Left leg
        this.leftHip.position.set(0.25, -0.6, 0);
        this.leftHip.add(this.upperLeftLeg);
        this.upperLeftLeg.position.set(0, -0.35, 0);
        this.torso.add(this.leftHip);
        
        this.leftKnee.position.set(0, -0.7, 0);
        this.leftKnee.add(this.lowerLeftLeg);
        this.lowerLeftLeg.position.set(0, -0.3, 0);
        this.upperLeftLeg.add(this.leftKnee);
        
        // Add torso to the root of the model
        this.add(this.torso);
        
        // Center the model so the feet are at y=0
        this.torso.position.set(0, 1.85, 0); // Reduced height positioning
    }
    
    /**
     * Get the weapon attachment point for positioning weapons
     */
    getWeaponAttachmentPoint() {
        return this.weaponAttachment;
    }
    
    /**
     * Get world position of the weapon attachment point
     * @returns {THREE.Vector3} The world position of the weapon attachment
     */
    getWeaponWorldPosition() {
        const position = new THREE.Vector3();
        this.weaponAttachment.getWorldPosition(position);
        return position;
    }
    
    /**
     * Update animations based on elapsed time
     * @param {number} deltaTime - Time since last frame in seconds
     */
    update(deltaTime) {
        // Accumulate animation time
        this.animationState.time += deltaTime * this.config.animationSpeed;
        
        // Apply different animations based on state
        if (this.animationState.walking) {
            this.animateWalk(this.animationState.time);
        }
        
        if (this.animationState.idle && !this.isCurrentlyAiming && !this.isCurrentlyShooting && !this.animationState.walking) {
            this.animateIdle(this.animationState.time);
        }
    }
    
    /**
     * Idle animation - subtle swaying
     */
    animateIdle(time) {
        // Subtle body sway
        this.torso.rotation.y = Math.sin(time * 0.5) * 0.05;
        
        // Subtle arm swing
        this.leftShoulder.rotation.x = Math.sin(time * 0.5) * 0.05;
        this.rightShoulder.rotation.x = Math.sin(time * 0.5 + 0.5) * 0.05;
    }
    
    /**
     * Walking animation
     */
    animateWalk(time) {
        if (this.isCurrentlyAiming) {
            // If aiming, only animate legs
            this.rightHip.rotation.x = Math.sin(time * 3) * 0.4;
            this.leftHip.rotation.x = Math.sin(time * 3 + Math.PI) * 0.4;
            
            // Knee bend when leg is forward
            this.rightKnee.rotation.x = Math.abs(Math.sin(time * 3)) * 0.3;
            this.leftKnee.rotation.x = Math.abs(Math.sin(time * 3 + Math.PI)) * 0.3;
            
            // Subtle torso rotation
            this.torso.rotation.y = Math.sin(time * 1.5) * 0.03;
        }
        else {
            // Full walking animation when not aiming
            // Leg swing
            this.rightHip.rotation.x = Math.sin(time * 3) * 0.4;
            this.leftHip.rotation.x = Math.sin(time * 3 + Math.PI) * 0.4;
            
            // Knee bend when leg is forward
            this.rightKnee.rotation.x = Math.abs(Math.sin(time * 3)) * 0.3;
            this.leftKnee.rotation.x = Math.abs(Math.sin(time * 3 + Math.PI)) * 0.3;
            
            // Arm swing (opposite to legs)
            this.rightShoulder.rotation.x = Math.sin(time * 3 + Math.PI) * 0.25;
            this.leftShoulder.rotation.x = Math.sin(time * 3) * 0.25;
            
            // Subtle torso rotation
            this.torso.rotation.y = Math.sin(time * 1.5) * 0.05;
        }
    }
    
    /**
     * Set animation state
     */
    setAnimationState(state) {
        this.animationState = {
            ...this.animationState,
            ...state
        };
    }
    
    /**
     * Make the player look in a specific direction
     * @param {THREE.Vector3} direction - The direction to look at
     */
    lookAt(direction) {
        // Normalize direction to ensure consistent behavior
        const normalizedDirection = direction.clone().normalize();
        
        // Calculate the angle between the direction and positive Z-axis
        const targetAngle = Math.atan2(normalizedDirection.x, normalizedDirection.z);
        
        // Set the rotation directly (this will make the backpack face away from the camera)
        this.rotation.y = targetAngle;
    }
    
    /**
     * Position the model's arms for two-handed precision aiming (right-click)
     * @param {boolean} isAiming - Whether the player is currently aiming
     */
    setAiming(isAiming) {
        // Clear any previous timeout
        if (this.aimingTimeout) {
            clearTimeout(this.aimingTimeout);
            this.aimingTimeout = null;
        }
        
        if (isAiming) {
            // Two-handed precision aiming pose
            this.rightShoulder.rotation.x = -0.7; // Raise arm higher
            this.rightShoulder.rotation.z = 0.3;
            this.rightElbow.rotation.x = -0.9; // Bend elbow more
            
            // Support arm position
            this.leftShoulder.rotation.x = -0.5;
            this.leftShoulder.rotation.z = -0.4;
            this.leftElbow.rotation.x = -0.7;
            
            this.animationState.aiming = true;
            this.isCurrentlyAiming = true;
        } else {
            // Return arms to sides when not aiming
            this.rightShoulder.rotation.x = -0.2; // Slight bend to hold gun
            this.rightShoulder.rotation.z = 0.1;  // Slight outward angle
            this.rightElbow.rotation.x = -0.3;    // Slight bend at elbow
            
            // Left arm completely relaxed
            this.leftShoulder.rotation.x = 0;
            this.leftShoulder.rotation.z = 0;
            this.leftElbow.rotation.x = 0;
            
            this.animationState.aiming = false;
            this.isCurrentlyAiming = false;
        }
    }
    
    /**
     * Triggers the one-handed shooting animation (left-click)
     */
    triggerShootingAnimation() {
        // Clear any previous timeout
        if (this.shootingTimeout) {
            clearTimeout(this.shootingTimeout);
            this.shootingTimeout = null;
        }
        
        // Quick one-handed shooting pose
        this.rightShoulder.rotation.x = -0.8; // Raise arm higher
        this.rightShoulder.rotation.z = 0.2;
        this.rightElbow.rotation.x = -0.5; // Slight bend at elbow
        
        // Left arm stays down during one-handed shooting
        this.leftShoulder.rotation.x = 0;
        this.leftShoulder.rotation.z = 0;
        this.leftElbow.rotation.x = 0;
        
        this.animationState.shooting = true;
        this.isCurrentlyShooting = true;
        
        // Set a timeout to lower the arm after 500ms (quicker than aiming)
        this.shootingTimeout = setTimeout(() => {
            // Return to relaxed position with gun still in hand
            this.rightShoulder.rotation.x = -0.2; // Slight bend to hold gun
            this.rightShoulder.rotation.z = 0.1;  // Slight outward angle
            this.rightElbow.rotation.x = -0.3;    // Slight bend at elbow
            
            this.animationState.shooting = false;
            this.isCurrentlyShooting = false;
        }, 500);
    }
    
    /**
     * Triggers the pistol reload animation
     */
    triggerReloadAnimation() {
        // Clear any previous timeouts
        if (this.reloadTimeout) {
            clearTimeout(this.reloadTimeout);
            this.reloadTimeout = null;
        }
        
        if (this.shootingTimeout) {
            clearTimeout(this.shootingTimeout);
            this.shootingTimeout = null;
        }
        
        console.log("Playing reload animation");
        
        // Initial position - bring pistol closer to body
        this.rightShoulder.rotation.x = -0.4;
        this.rightShoulder.rotation.z = 0.4;
        this.rightElbow.rotation.x = -0.8;
        
        // Left hand reaches for magazine
        this.leftShoulder.rotation.x = -0.6;
        this.leftShoulder.rotation.z = -0.3;
        this.leftElbow.rotation.x = -0.9;
        
        this.isCurrentlyReloading = true;
        
        // After a short delay, show magazine removal motion
        setTimeout(() => {
            // Right arm holds gun steady
            this.rightShoulder.rotation.x = -0.5;
            this.rightShoulder.rotation.z = 0.3;
            
            // Left arm pulls down slightly (removing magazine)
            this.leftShoulder.rotation.x = -0.3;
            this.leftShoulder.rotation.z = -0.4;
            this.leftElbow.rotation.x = -0.7;
        }, 800);  // Phase 1: 800ms (was 400ms)
        
        // After another delay, show inserting new magazine
        setTimeout(() => {
            // Right arm steady
            this.rightShoulder.rotation.x = -0.5;
            this.rightShoulder.rotation.z = 0.3;
            
            // Left arm pushes up (inserting new magazine)
            this.leftShoulder.rotation.x = -0.6;
            this.leftShoulder.rotation.z = -0.3;
            this.leftElbow.rotation.x = -0.9;
        }, 2000);  // Phase 2: 2000ms (was 800ms)
        
        // Add magazine tap animation (new phase)
        setTimeout(() => {
            // Quick tap motion with left hand
            this.leftShoulder.rotation.x = -0.5;
            this.leftShoulder.rotation.z = -0.3;
            this.leftElbow.rotation.x = -0.8;
        }, 3000);  // Phase 3: 3000ms (new phase)
        
        // After final delay, return to ready position
        this.reloadTimeout = setTimeout(() => {
            // Return to default position with gun at side
            this.rightShoulder.rotation.x = -0.2;
            this.rightShoulder.rotation.z = 0.1;
            this.rightElbow.rotation.x = -0.3;
            
            // Left arm relaxed
            this.leftShoulder.rotation.x = 0;
            this.leftShoulder.rotation.z = 0;
            this.leftElbow.rotation.x = 0;
            
            this.isCurrentlyReloading = false;
        }, 3800);  // Final phase: 3800ms (was 1300ms)
    }
    
    /**
     * Triggers the punch animation - extend arm forward for a punch
     */
    triggerPunchAnimation() {
        console.log("PlayerModel.triggerPunchAnimation called");
        // Clear any previous animation timeout
        if (this.punchTimeout) {
            clearTimeout(this.punchTimeout);
            this.punchTimeout = null;
        }
        
        // Store original arm positions
        if (!this.originalArmPosition) {
            this.originalArmPosition = {
                rightShoulderX: this.rightShoulder.rotation.x,
                rightShoulderZ: this.rightShoulder.rotation.z,
                rightElbowX: this.rightElbow.rotation.x
            };
            console.log("Stored original arm positions:", this.originalArmPosition);
        }
        
        console.log("Applying punch animation");
        // Punch animation - right arm extends forward quickly
        this.rightShoulder.rotation.x = -1.2; // Rotate arm forward
        this.rightShoulder.rotation.z = 0.2;  // Slight outward angle
        this.rightElbow.rotation.x = -0.3;    // Slight bend at elbow
        
        // Reset animation after a short delay
        this.punchTimeout = setTimeout(() => {
            console.log("Resetting punch animation");
            // Return to neutral stance or previous pose
            if (this.animationState.aiming) {
                // Return to aiming stance
                this.setAiming(true);
            } else {
                // Return to neutral or walking
                this.rightShoulder.rotation.x = this.originalArmPosition?.rightShoulderX || 0;
                this.rightShoulder.rotation.z = this.originalArmPosition?.rightShoulderZ || 0;
                this.rightElbow.rotation.x = this.originalArmPosition?.rightElbowX || 0;
            }
            
            this.punchTimeout = null;
        }, 300); // 300ms for punch animation
    }
    
    /**
     * Trigger a death animation with ragdoll effect
     * Simulates physics by applying rotations to body parts
     */
    startDeathAnimation() {
        // Stop any current animations
        this.animationState.walking = false;
        this.animationState.aiming = false;
        this.animationState.shooting = false;
        this.animationState.idle = false;
        this.animationState.dead = true;
        
        // Store initial state
        const startTime = performance.now();
        const initialPositions = {
            torso: this.torso.position.clone(),
            head: this.head.rotation.clone(),
            rightShoulder: this.rightShoulder.rotation.clone(),
            leftShoulder: this.leftShoulder.rotation.clone(),
            rightElbow: this.rightElbow.rotation.clone(),
            leftElbow: this.leftElbow.rotation.clone(),
            rightHip: this.rightHip.rotation.clone(),
            leftHip: this.leftHip.rotation.clone(),
            rightKnee: this.rightKnee.rotation.clone(),
            leftKnee: this.leftKnee.rotation.clone()
        };
        
        // Generate random fall direction
        const fallDirection = Math.random() > 0.5 ? 1 : -1;
        
        // Choose a random death pose type
        const deathPoseType = Math.floor(Math.random() * 3);
        
        // Define joint limits to create realistic ragdoll constraints
        const jointLimits = {
            shoulder: { min: -Math.PI/2, max: Math.PI/2 },
            elbow: { min: -Math.PI/1.5, max: 0 },
            hip: { min: -Math.PI/2, max: Math.PI/3 },
            knee: { min: 0, max: Math.PI/1.5 }
        };
        
        // Define velocities and accelerations for joints (simulated physics)
        const jointPhysics = {
            head: { vel: 0, acc: 0.001 * fallDirection },
            rightShoulder: { vel: 0, acc: 0.002 * fallDirection },
            leftShoulder: { vel: 0, acc: 0.0015 * fallDirection },
            rightElbow: { vel: 0, acc: 0.001 },
            leftElbow: { vel: 0, acc: 0.0012 },
            rightHip: { vel: 0, acc: 0.0025 * fallDirection },
            leftHip: { vel: 0, acc: 0.002 * fallDirection },
            rightKnee: { vel: 0, acc: 0.001 },
            leftKnee: { vel: 0, acc: 0.0015 }
        };
        
        // Create animation function
        const animateRagdoll = () => {
            // Get elapsed time
            const elapsed = performance.now() - startTime;
            const duration = 2000; // 2 seconds for full animation
            const progress = Math.min(1, elapsed / duration);
            
            // Early phase - initial collapse
            if (progress < 0.4) {
                // Body starting to fall
                this.torso.rotation.x = initialPositions.torso.x + (0.3 * progress);
                this.torso.position.y = initialPositions.torso.y - (progress * 0.5);
            } 
            // Mid phase - main fall
            else if (progress < 0.7) {
                // Calculate fall progress within this phase
                const fallProgress = (progress - 0.4) / 0.3;
                
                // Body falls to ground
                this.torso.rotation.z = fallProgress * Math.PI/2 * fallDirection;
                this.torso.rotation.x = 0.3 - (fallProgress * 0.2);
                this.torso.position.y = initialPositions.torso.y - 0.5 - (fallProgress * 1.0);
            } 
            // Final phase - settle on ground with secondary motion
            else {
                // Calculate settle progress
                const settleProgress = (progress - 0.7) / 0.3;
                
                // Body on ground with small settling movements
                this.torso.rotation.z = Math.PI/2 * fallDirection + (Math.sin(settleProgress * Math.PI * 2) * 0.1);
                this.torso.position.y = initialPositions.torso.y - 1.5 + (Math.sin(settleProgress * Math.PI) * 0.1);
            }
            
            // Apply simulated physics to joints throughout animation
            Object.keys(jointPhysics).forEach(joint => {
                // Apply accumulated velocity to joint
                jointPhysics[joint].vel += jointPhysics[joint].acc;
                
                // Different joint behaviors based on death pose type
                switch(deathPoseType) {
                    case 0: // Sprawled pose
                        // Limbs spread out
                        if (joint === 'head') {
                            this.neck.rotation.z = Math.min(jointLimits.shoulder.max, 
                                initialPositions.head.z + jointPhysics[joint].vel * elapsed * 0.05) * fallDirection;
                        } else if (joint.includes('Shoulder')) {
                            this[joint].rotation.z = Math.min(jointLimits.shoulder.max, 
                                initialPositions[joint].z + jointPhysics[joint].vel * elapsed * 0.1) * fallDirection;
                        } else if (joint.includes('Elbow')) {
                            this[joint].rotation.x = Math.max(jointLimits.elbow.min, 
                                initialPositions[joint].x + jointPhysics[joint].vel * elapsed * 0.05);
                        } else if (joint.includes('Hip')) {
                            this[joint].rotation.z = Math.min(jointLimits.hip.max, 
                                initialPositions[joint].z + jointPhysics[joint].vel * elapsed * 0.1) * fallDirection;
                            this[joint].rotation.x = Math.min(jointLimits.hip.max, 
                                initialPositions[joint].x + jointPhysics[joint].vel * elapsed * 0.05);
                        } else if (joint.includes('Knee')) {
                            this[joint].rotation.x = Math.min(jointLimits.knee.max, 
                                initialPositions[joint].x + jointPhysics[joint].vel * elapsed * 0.05);
                        }
                        break;
                        
                    case 1: // Fetal position
                        // Limbs curled inward
                        if (joint === 'head') {
                            this.neck.rotation.x = Math.min(jointLimits.shoulder.max, 
                                initialPositions.head.x + jointPhysics[joint].vel * elapsed * 0.05);
                        } else if (joint.includes('Shoulder')) {
                            this[joint].rotation.z = Math.min(jointLimits.shoulder.max, 
                                initialPositions[joint].z - jointPhysics[joint].vel * elapsed * 0.05) * fallDirection;
                        } else if (joint.includes('Elbow')) {
                            this[joint].rotation.x = Math.min(0, 
                                initialPositions[joint].x - jointPhysics[joint].vel * elapsed * 0.1);
                        } else if (joint.includes('Hip')) {
                            this[joint].rotation.x = Math.max(0, 
                                initialPositions[joint].x + jointPhysics[joint].vel * elapsed * 0.1);
                        } else if (joint.includes('Knee')) {
                            this[joint].rotation.x = Math.max(0, 
                                initialPositions[joint].x + jointPhysics[joint].vel * elapsed * 0.15);
                        }
                        break;
                        
                    case 2: // Twisted fall
                        // Body twisted
                        if (joint === 'head') {
                            this.neck.rotation.y = Math.min(jointLimits.shoulder.max, 
                                initialPositions.head.y + jointPhysics[joint].vel * elapsed * 0.05) * fallDirection;
                        } else if (joint.includes('Shoulder')) {
                            this[joint].rotation.y = Math.min(jointLimits.shoulder.max, 
                                initialPositions[joint].y + jointPhysics[joint].vel * elapsed * 0.08) * fallDirection;
                        } else if (joint.includes('Elbow')) {
                            this[joint].rotation.x = Math.min(0, 
                                initialPositions[joint].x + jointPhysics[joint].vel * elapsed * 0.1);
                        } else if (joint.includes('Hip')) {
                            this[joint].rotation.y = Math.min(jointLimits.hip.max, 
                                initialPositions[joint].y - jointPhysics[joint].vel * elapsed * 0.08) * fallDirection;
                        } else if (joint.includes('Knee')) {
                            this[joint].rotation.x = Math.min(jointLimits.knee.max, 
                                initialPositions[joint].x + jointPhysics[joint].vel * elapsed * 0.1);
                        }
                        break;
                }
                
                // Add subtle oscillation for more realistic motion
                const oscillation = Math.sin(elapsed * 0.01) * 0.02 * (1 - progress);
                if (this[joint]) {
                    this[joint].rotation.x += oscillation;
                    this[joint].rotation.z += oscillation;
                }
            });
            
            // Continue animation if not complete
            if (progress < 1) {
                requestAnimationFrame(animateRagdoll);
            }
        };
        
        // Start animation
        requestAnimationFrame(animateRagdoll);
    }
} 